import { useRef, useState, useCallback } from 'react';

/**
 * Surface with a cursor-tracking radial glow, lift on hover, and a subtle 3D tilt.
 * Exposes `hovered` to children via a render-prop so the card can reveal content.
 */
export default function GlowCard({ children, style: extraStyle, tilt = true }) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ x: -9999, y: -9999 });
  const [tiltXY, setTiltXY] = useState({ rx: 0, ry: 0 });
  const [hovered, setHovered] = useState(false);

  const onMove = useCallback((e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    const x = e.clientX - r.left;
    const y = e.clientY - r.top;
    setPos({ x, y });
    if (tilt) {
      const px = x / r.width - 0.5;
      const py = y / r.height - 0.5;
      setTiltXY({ rx: -py * 4, ry: px * 4 });
    }
  }, [tilt]);

  const reset = () => {
    setHovered(false);
    setPos({ x: -9999, y: -9999 });
    setTiltXY({ rx: 0, ry: 0 });
  };

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={reset}
      style={{
        position: 'relative',
        borderRadius: 16,
        backgroundColor: 'var(--tandm-surface)',
        border: hovered ? '1px solid var(--tandm-accent-light)' : '1px solid var(--tandm-line)',
        boxShadow: hovered ? 'var(--tandm-shadow-lift)' : 'var(--tandm-shadow-soft)',
        transform: hovered
          ? `perspective(900px) rotateX(${tiltXY.rx}deg) rotateY(${tiltXY.ry}deg) translateY(-3px)`
          : 'perspective(900px) rotateX(0deg) rotateY(0deg) translateY(0)',
        transformStyle: 'preserve-3d',
        transition: 'transform 0.22s var(--tandm-ease), border-color 0.2s, box-shadow 0.25s',
        overflow: 'hidden',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        ...extraStyle,
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: 16,
          pointerEvents: 'none',
          zIndex: 1,
          opacity: hovered ? 1 : 0,
          transition: 'opacity 0.25s ease',
          background: `radial-gradient(240px circle at ${pos.x}px ${pos.y}px,
            rgba(78,108,126,0.10) 0%,
            rgba(108,150,156,0.04) 45%,
            transparent 70%)`,
        }}
      />
      <div style={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', flex: 1 }}>
        {typeof children === 'function' ? children(hovered) : children}
      </div>
    </div>
  );
}
